import type { LandRequest, WorkPackage, WorkPackageStatus } from "@botallion/contracts";
import { packageCostLabel, runTotalLabel } from "@botallion/metering/hooks/dashboard";
import { nameOf, withNames } from "../describe.ts";
import { html, type Html } from "../html.ts";
import { currentPlan, type DashboardState } from "../state.ts";
import { chip, clock, costChip, empty, shortSha, type Tone, type ViewContext } from "./common.ts";

const COLUMNS: { status: WorkPackageStatus[]; title: string }[] = [
  { status: ["draft", "approved"], title: "Planned" },
  { status: ["dispatched"], title: "Dispatched" },
  { status: ["in_progress"], title: "In progress" },
  { status: ["verifying", "failed"], title: "Verifying" },
  { status: ["landed"], title: "Landed" },
];

const LAND_TONE: Record<LandRequest["status"], Tone> = { pending: "warn", approved: "info", rejected: "block", merged: "good", conflict: "block", failed: "block" };

function latestLand(state: DashboardState, wpId: string): LandRequest | undefined {
  return Object.values(state.lands)
    .filter((l) => l.wpId === wpId)
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt) || a.id.localeCompare(b.id))
    .at(-1);
}

function card(ctx: ViewContext, p: WorkPackage): Html {
  const { state } = ctx;
  const session = p.sessionId ? state.sessions[p.sessionId] : undefined;
  const verdict = state.verdicts.filter((v) => v.wpId === p.id).at(-1);
  const met = verdict ? verdict.results.filter((r) => r.met).length : 0;
  const land = latestLand(state, p.id);
  return html`<li class="wp-card ${p.status}">
    <div class="row"><strong>${p.id}</strong>${p.status === "failed" ? chip("failed verification", "block") : p.status === "verifying" ? chip("verifying", "warn") : ""}</div>
    <div class="title">${p.title}</div>
    <div class="meta">${session ? html`agent <strong>${session.agentName}</strong>` : html`<span class="muted">no agent yet</span>`}</div>
    ${p.dependsOn.length
      ? html`<div class="deps">needs ${p.dependsOn.map((d, i) => html`${i ? ", " : ""}<span class="${state.packages[d]?.status === "landed" ? "dep-done" : "dep-wait"}">${d}${state.packages[d]?.status === "landed" ? " (landed)" : ""}</span>`)}</div>`
      : ""}
    <div class="meta">criteria: ${verdict ? `${met} of ${p.acceptanceCriteria.length} met` : `${p.acceptanceCriteria.length}, not verified yet`}</div>
    ${ctx.costs !== undefined ? html`<div class="meta">cost ${costChip(packageCostLabel(ctx.costs, p.id))}</div>` : ""}
    ${land ? html`<div class="meta">land ${shortSha(land.commit)} ${chip(land.status, LAND_TONE[land.status])}${land.reasons.length ? html`<div class="muted">${withNames(state, land.reasons.join("; "))}</div>` : ""}</div>` : ""}
  </li>`;
}

/** View 4: packages by status, with dependencies. */
export function planView(ctx: ViewContext): Html {
  const { state } = ctx;
  const plans = Object.values(state.plans).map((p) => currentPlan(state, p));
  if (!plans.length) return empty("No plan yet. Write a spec and press Plan it.");
  return html`${plans.map((plan) => html`<div class="plan">
    <div class="plan-head">
      <h3>${plan.title} <span class="muted">(${plan.specId})</span></h3>
      ${ctx.costs !== undefined ? costChip(runTotalLabel(ctx.costs, plan.specId)) : ""}
      ${plan.approvedBy ? chip(`approved by ${nameOf(state, plan.approvedBy)} at ${clock(plan.approvedAt)}`, "good") : chip(ctx.approver.canApprove ? "draft: waiting for your approval" : "draft: waiting for approval", "human")}
    </div>
    ${plan.questions.length
      ? html`<ul class="questions">${plan.questions.map((q) => html`<li>${q.blocking ? chip("blocking", "warn") : chip("question", "muted")} ${q.text}
          ${q.answer ? html`<div class="answer">Answer: ${q.answer}</div>` : html`<div class="muted">not answered yet</div>`}</li>`)}</ul>`
      : ""}
    <div class="board">${COLUMNS.map((col) => {
      const items = plan.packages.filter((p) => col.status.includes(p.status));
      return html`<div class="column"><h4>${col.title} <span class="muted">${items.length}</span></h4><ul>${items.map((p) => card(ctx, p))}</ul></div>`;
    })}</div>
    ${plan.sequencingNotes.length ? html`<details class="notes"><summary>Sequencing notes</summary><ul>${plan.sequencingNotes.map((n) => html`<li>${n}</li>`)}</ul></details>` : ""}
  </div>`)}`;
}
